/**
 * Gerador do template AR Blueprint (Relatório de Avaliação de Risco).
 *
 * Baseado no arquivo "AR - blueprint.pdf" analisado em 2026-05-20. Esta
 * primeira versão implementa a estrutura documental e usa os campos atuais da
 * apreciação, com extensões opcionais em `camposCliente.ar`.
 */

import { DadosApreciacao, Risco } from '../core/latex-types';
import { escapeLatex, escapeLatexComQuebras } from '../core/latex-utils';
import {
  AR_NORMAS_INTERNACIONAIS_PADRAO,
  AR_NORMAS_NACIONAIS_PADRAO,
  criarArCamposDocumentoDefault,
} from '@/types/ar-document';

type CorRiscoAr = 'arGreen' | 'arYellow' | 'arOrange' | 'arRed';

interface ClassificacaoAr {
  texto: string;
  cor: CorRiscoAr;
  textoCor: 'black' | 'white';
}

interface AvaliacaoModoAr {
  modo: string;
  probabilidade?: number | null;
  frequencia?: number | null;
  gravidade?: number | null;
  exposicao?: number | null;
  hrn?: number | null;
}

const AR_ASSETS = {
  coverPhoto: 'ar-cover-photo.png',
  coverGear: 'ar-cover-gear.png',
  coverLogo: 'ar-cover-logo.png',
  headerLogo: 'ar-header-logo.png',
} as const;

function arCampos(dados: DadosApreciacao) {
  return dados.camposCliente?.ar || criarArCamposDocumentoDefault(dados.projeto?.nome || '');
}

function texto(valor: unknown, fallback = 'Not Available'): string {
  if (valor === undefined || valor === null) return fallback;
  const str = String(valor).trim();
  return str || fallback;
}

function numero(value: unknown, casas = 3): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: casas });
}

function numeroHrn(value: unknown, casas = 3): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  if (n > 0 && n < 1) return '<1';
  return numero(n, casas);
}

function formatarDataAr(data?: string): string {
  const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
  const fonte = data || new Date().toISOString();
  const str = String(fonte).substring(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [ano, mes, dia] = str.split('-').map(Number);
    return `${dia} ${meses[mes - 1] || meses[0]} ${ano}`;
  }
  return str;
}

function obterDocumentoId(dados: DadosApreciacao): string {
  const ar = arCampos(dados);
  return ar.idDocumento || dados.numeroDocumento || dados.numeroEmitente || dados.numero || 'PRJ00000000-V1';
}

function obterTituloRelatorio(dados: DadosApreciacao): string {
  const ar = arCampos(dados);
  return ar.nomeRelatorio || dados.titulo || dados.nomeMaquina || dados.maquina?.nome || 'Relatório de Avaliação de Risco';
}

function obterCliente(dados: DadosApreciacao): string {
  const ar = arCampos(dados);
  return ar.cliente || dados.projeto?.nome || dados.maquina?.site || 'Cliente';
}

function obterDocumentoRodape(dados: DadosApreciacao): string {
  return obterDocumentoId(dados).replace(/-V(\d+)$/i, ' V$1');
}

function obterMaquina(dados: DadosApreciacao): string {
  return dados.nomeMaquina || dados.maquina?.nome || dados.maquina?.descricao || dados.titulo || 'Máquina';
}

function formatarNomeExibicao(valor: string): string {
  const textoLimpo = texto(valor, '').trim();
  if (!textoLimpo) return '';
  if (!textoLimpo.includes(',')) return textoLimpo;
  const [sobrenome, prenome] = textoLimpo.split(',').map((parte) => parte.trim()).filter(Boolean);
  return [prenome, sobrenome].filter(Boolean).join(' ');
}

function obterTituloWorkflow(dados: DadosApreciacao): string {
  const criado = formatarNomeExibicao(obterCriadoPor(dados).nome);
  const aprovado = formatarNomeExibicao(obterAprovadoPor(dados).nome);
  const base = [criado, aprovado].filter(Boolean).join(' e ');
  const tipo = [dados.maquina?.fabricante, dados.tipoMaquina].filter(Boolean).join(' ') || dados.tipoMaquina || 'Máquina';
  return `${base || obterCliente(dados)} - ${tipo}`;
}

function fotosMaquina(dados: DadosApreciacao): string[] {
  const detalhes = arCampos(dados).detalhesMaquina as unknown as Record<string, unknown>;
  const lista = [
    detalhes.foto1,
    detalhes.foto2,
    detalhes.foto3,
    detalhes.foto4,
    ...(Array.isArray(detalhes.fotos) ? detalhes.fotos : []),
  ].filter(Boolean) as string[];
  return lista;
}

function legendaFotoPrincipal(risco: Risco): string {
  const extras = (risco as any).origemEspecifica || {};
  return texto(extras.legendaFotoPrincipal || extras.legendaFoto || risco.eventoPerigoso, '');
}

function legendasFotosAdicionais(risco: Risco): string[] {
  const extras = (risco as any).origemEspecifica || {};
  if (Array.isArray(extras.legendasFotosAdicionais)) {
    return extras.legendasFotosAdicionais.map((item: unknown) => texto(item, '')).filter(Boolean);
  }
  return [];
}

function fotosMaquinaFallback(dados: DadosApreciacao, riscos: Risco[]): string[] {
  const diretas = fotosMaquina(dados);
  if (diretas.length > 0) return diretas;
  const fotos = riscos.flatMap((risco) => fotosRisco(risco));
  return [...new Set(fotos)].slice(0, 2);
}

function obterCriadoPor(dados: DadosApreciacao) {
  const ar = arCampos(dados);
  const origem = ar.criadoPor.nome
    ? ar.criadoPor
    : dados.elaborador || dados.elaboradorExterno || dados.executante || dados.executanteExterno || {};
  return {
    nome: texto((origem as any).nome, 'Not Available'),
    funcao: texto((origem as any).funcao || (origem as any).cargo, 'Not Available'),
    contato: texto((origem as any).contato, ''),
  };
}

function obterAprovadoPor(dados: DadosApreciacao) {
  const ar = arCampos(dados);
  const origem = ar.aprovadoPor.nome
    ? ar.aprovadoPor
    : dados.aprovador || dados.aprovadorExterno || dados.responsavelTecnico || dados.responsavelTecnicoExterno || {};
  return {
    nome: texto((origem as any).nome, 'Not Available'),
    funcao: texto((origem as any).funcao || (origem as any).cargo, 'Not Available'),
    contato: texto((origem as any).contato, ''),
  };
}

function classificarAr(hrn?: number | null): ClassificacaoAr {
  const valor = Number(hrn || 0);
  if (valor < 5) return { texto: 'Admissível', cor: 'arGreen', textoCor: 'black' };
  if (valor < 10) return { texto: 'Admissível', cor: 'arGreen', textoCor: 'black' };
  if (valor < 15) return { texto: 'Baixo', cor: 'arYellow', textoCor: 'black' };
  if (valor < 50) return { texto: 'Significativo', cor: 'arOrange', textoCor: 'black' };
  return { texto: 'Inaceitável', cor: 'arRed', textoCor: 'white' };
}

function celulaHrnResumo(hrn?: number | null): string {
  const classificacao = classificarAr(hrn);
  const textoCor = classificacao.textoCor === 'white' ? '\\textcolor{white}' : '\\textcolor{black}';
  return `\\cellcolor{${classificacao.cor}}${textoCor}{${numeroHrn(hrn)}}`;
}

function descricaoHrn(tipo: 'LO' | 'FE' | 'DPH' | 'NPE', valor?: number | null): string {
  const tabelas: Record<typeof tipo, Record<string, string>> = {
    LO: {
      '0.033': 'Quase Impossível',
      '0.5': 'Altamente improvável',
      '1': 'Altamente improvável',
      '1.5': 'Improvável',
      '2': 'Possível, mas incomum',
      '5': 'Alguma chance',
      '8': 'Provável',
      '10': 'Muito provável',
      '15': 'Certo',
    },
    FE: {
      '0.5': 'Anualmente',
      '1': 'Mensalmente',
      '1.5': 'Semanalmente',
      '2.5': 'Diariamente',
      '4': 'Por hora',
      '5': 'Constantemente',
    },
    DPH: {
      '0.1': 'Arranhão / contusão / escoriação',
      '0.5': 'Dilaceração / cortes / enfermidade leve',
      '1': 'Fratura ossos menores / enfermidade moderada',
      '2': 'Fratura de osso(s) maior(es) / enfermidade grave',
      '4': 'Perda de 1 membro / olho',
      '8': 'Perda de 2 membros / olhos',
      '12': 'Enfermidade permanente ou crítica',
      '15': 'Fatalidade',
    },
    NPE: {
      '1': '1-2 pessoas',
      '2': '3-7 pessoas',
      '4': '8-15 pessoas',
      '8': '16-50 pessoas',
      '12': 'Mais que 50 pessoas',
    },
  };
  const key = String(Number(valor || 0));
  const descricao = tabelas[tipo][key] || '-';
  return `${descricao} (${numero(valor)})`;
}

function fotosRisco(risco: Risco): string[] {
  return [risco.fotoAtualUrl1, risco.fotoAtualUrl2, risco.fotoAtualUrl3, risco.fotoAtualUrl4].filter(Boolean) as string[];
}

function modosRisco(dados: DadosApreciacao, risco: Risco): string[] {
  const ar = arCampos(dados);
  const extras = (risco as any).origemEspecifica || {};
  const avaliacoesIniciais = Array.isArray(extras.avaliacoesPorModo) ? extras.avaliacoesPorModo : [];
  const avaliacoesResiduais = Array.isArray(extras.avaliacoesResiduaisPorModo) ? extras.avaliacoesResiduaisPorModo : [];
  const modosAvaliados = [...avaliacoesIniciais, ...avaliacoesResiduais]
    .map((item) => texto(item?.modo, ''))
    .filter(Boolean);
  if (modosAvaliados.length > 0) return [...new Set(modosAvaliados)];
  const origem = (risco.cicloVidaArray && risco.cicloVidaArray.length > 0)
    ? risco.cicloVidaArray
    : risco.cicloVida
      ? risco.cicloVida.split(/[,;]/)
      : ar.limitesMaquina.modosMaquina;
  const modos = origem.map((item) => item.trim()).filter(Boolean);
  return modos.length > 0 ? modos : ['Normal'];
}

function obterAvaliacaoModo(risco: Risco, modo: string, residual = false): AvaliacaoModoAr {
  const extras = (risco as any).origemEspecifica || {};
  const chave = residual ? 'avaliacoesResiduaisPorModo' : 'avaliacoesPorModo';
  const lista = Array.isArray(extras[chave]) ? extras[chave] as AvaliacaoModoAr[] : [];
  const encontrada = lista.find((item) => texto(item?.modo, '').toLowerCase() === texto(modo, '').toLowerCase());
  if (encontrada) return encontrada;
  return {
    modo,
    probabilidade: residual ? risco.probabilidadeFuturo : risco.probabilidadeAtual,
    frequencia: residual ? risco.frequenciaFuturo : risco.frequenciaAtual,
    gravidade: residual ? risco.gravidadeFuturo : risco.gravidadeAtual,
    exposicao: residual ? risco.exposicaoFuturo : risco.exposicaoAtual,
    hrn: residual ? risco.hrnFuturo : risco.hrnAtual,
  };
}

function gerarPreambuloAr(dados: DadosApreciacao): string {
  const documento = escapeLatex(obterDocumentoRodape(dados));
  const data = escapeLatex(formatarDataAr(dados.dataEmissao));
  return `\\documentclass[a4paper,9pt]{article}
\\usepackage{graphicx}
\\DeclareGraphicsExtensions{.png,.jpg,.jpeg,.pdf}
\\usepackage[table]{xcolor}
\\usepackage{array}
\\usepackage{tabularx}
\\usepackage{longtable}
\\usepackage{fancyhdr}
\\usepackage{lastpage}
\\usepackage{enumitem}
\\usepackage{float}
\\usepackage{hyperref}
\\usepackage{tocloft}
\\usepackage{fontspec}
\\usepackage{tikz}
\\usetikzlibrary{calc}
\\usepackage{titlesec}
\\usepackage[a4paper,top=2.35cm,left=1.45cm,right=1.35cm,bottom=1.8cm]{geometry}

\\setmainfont{Liberation Sans}
\\definecolor{arBlue}{RGB}{0,63,128}
\\definecolor{arCyan}{RGB}{55,190,207}
\\definecolor{arCoverBlue}{RGB}{10,64,131}
\\definecolor{arGray}{RGB}{235,238,240}
\\definecolor{arGrayLine}{RGB}{210,215,218}
\\definecolor{arGrayDark}{RGB}{85,91,97}
\\definecolor{arGreen}{RGB}{198,224,180}
\\definecolor{arYellow}{RGB}{255,242,204}
\\definecolor{arOrange}{RGB}{248,203,173}
\\definecolor{arRed}{RGB}{224,102,102}

\\setlength\\parindent{0pt}
\\setlength{\\parskip}{3pt}
\\renewcommand{\\arraystretch}{1.35}
\\setlength{\\tabcolsep}{4pt}
\\setlist[itemize]{leftmargin=*,itemsep=1pt,topsep=2pt}

\\pagestyle{fancy}
\\fancyhf{}
\\setlength{\\headheight}{1.15cm}
\\fancyhead[L]{\\raisebox{-0.12cm}{\\includegraphics[height=0.78cm]{${AR_ASSETS.headerLogo}}}}
\\fancyhead[C]{\\raisebox{-0.95cm}{\\textcolor{arGrayLine}{\\rule{\\linewidth}{0.35pt}}}}
\\fancyfoot[L]{\\scriptsize ${documento}}
\\fancyfoot[C]{\\scriptsize ${data}}
\\fancyfoot[R]{\\scriptsize Page : \\thepage/\\pageref{LastPage}}
\\renewcommand{\\headrulewidth}{0pt}
\\renewcommand{\\footrulewidth}{0pt}
\\fancypagestyle{plain}{\\fancyhf{}\\fancyfoot[L]{\\scriptsize ${documento}}\\fancyfoot[C]{\\scriptsize ${data}}\\fancyfoot[R]{\\scriptsize Page : \\thepage/\\pageref{LastPage}}\\renewcommand{\\headrulewidth}{0pt}\\renewcommand{\\footrulewidth}{0pt}}

\\hypersetup{colorlinks=false,pdfborder={0 0 0},linktoc=all}
\\renewcommand{\\contentsname}{Índice}
\\renewcommand{\\cfttoctitlefont}{\\hfill\\Large\\bfseries}
\\renewcommand{\\cftaftertoctitle}{\\hfill}
\\setlength{\\cftbeforesecskip}{3pt}

\\titleformat*{\\section}{\\normalfont\\Large\\bfseries}
\\titleformat*{\\subsection}{\\normalfont\\large\\bfseries}
\\titlespacing*{\\section}{0pt}{7pt}{8pt}
\\titlespacing*{\\subsection}{0pt}{7pt}{5pt}

\\newcommand{\\arSection}[1]{\\clearpage\\noindent\\colorbox{arBlue}{\\parbox{\\dimexpr\\linewidth-2\\fboxsep}{\\color{white}\\large\\bfseries\\strut #1}}\\vspace{0.25cm}}
\\newcommand{\\arBlueBand}[1]{\\noindent\\colorbox{arBlue}{\\parbox{\\dimexpr\\linewidth-2\\fboxsep}{\\color{white}\\large\\bfseries\\strut #1}}\\vspace{0.12cm}}
\\newcommand{\\arCyanBand}[1]{\\noindent\\colorbox{arCyan}{\\parbox{\\dimexpr\\linewidth-2\\fboxsep}{\\bfseries\\strut #1}}\\vspace{0.08cm}}
\\newcommand{\\arTitle}[1]{\\vspace{0.15cm}\\noindent{\\large\\bfseries #1}\\vspace{0.1cm}}
\\newcommand{\\arBand}[1]{\\noindent\\colorbox{arGray}{\\parbox{\\dimexpr\\linewidth-2\\fboxsep}{\\bfseries\\strut #1}}\\vspace{0.08cm}}
`;
}

function gerarCapaAr(dados: DadosApreciacao): string {
  const titulo = obterTituloRelatorio(dados);
  return `
\\begin{titlepage}
\\thispagestyle{empty}
\\begin{tikzpicture}[remember picture,overlay]
  \\node[anchor=north,inner sep=0] at (current page.north)
    {\\includegraphics[width=\\paperwidth,height=14.45cm]{${AR_ASSETS.coverPhoto}}};
  \\fill[arCoverBlue] ([yshift=-14.45cm]current page.north west) rectangle (current page.south east);
  \\node[anchor=north east,inner sep=0,opacity=0.62] at ([xshift=-1.65cm,yshift=-17.35cm]current page.north east)
    {\\includegraphics[height=5.8cm]{${AR_ASSETS.coverGear}}};
  \\node[anchor=south east,inner sep=0] at ([xshift=-1.05cm,yshift=0.85cm]current page.south east)
    {\\includegraphics[width=5.0cm]{${AR_ASSETS.coverLogo}}};
  \\node[anchor=west,text=white,align=left,text width=13.2cm] at ([xshift=1.35cm,yshift=-15.95cm]current page.north west)
    {{\\fontsize{26}{31}\\selectfont\\bfseries \\textcolor{arCyan}{Relatório de Avaliação de}\\\\[-1pt]Risco}\\\\[0.72cm]
     {\\fontsize{15}{18}\\selectfont ${escapeLatex(titulo)}}};
  \\node[anchor=west,text=white,align=left,text width=12cm] at ([xshift=1.35cm,yshift=-21.0cm]current page.north west)
    {\\begin{tabular}{>{\\bfseries}p{2.75cm}p{8cm}}
      Cliente : & ${escapeLatex(obterCliente(dados))} \\\\[0.17cm]
      ID do documento : & ${escapeLatex(obterDocumentoId(dados))} \\\\[0.17cm]
      Data : & ${escapeLatex(formatarDataAr(dados.dataEmissao))} \\\\
    \\end{tabular}};
\\end{tikzpicture}
\\end{titlepage}
\\setcounter{page}{2}
`;
}

function gerarIndiceAr(dados: DadosApreciacao): string {
  const maquina = obterMaquina(dados);
  return `
\\thispagestyle{plain}
\\noindent\\colorbox{arBlue}{\\parbox{\\dimexpr\\linewidth-2\\fboxsep}{\\color{white}\\large\\bfseries\\strut Índice}}
\\vspace{0.15cm}
\\begin{tabularx}{\\linewidth}{|p{0.8cm}|X|p{2.0cm}|}
\\hline
\\rowcolor{arCyan}\\textbf{Nº.} & \\textbf{Título} & \\textbf{Página nº.} \\\\ \\hline
1 & Informações de Contato & 3 \\\\ \\hline
2 & Lista de Máquinas & 4 \\\\ \\hline
3 & Resumo da Classificação de Risco - Todas as Máquinas & 5 \\\\ \\hline
4 & Prefácio do Documento & 6 \\\\ \\hline
5 & Análise e Avaliação de Riscos & 20 \\\\ \\hline
 & \\hspace{0.6cm}5.1 . ${escapeLatex(maquina)} - Detalhes e limites da máquina & 20 \\\\ \\hline
 & \\hspace{1.2cm}\\textbullet\\ Resumo de Riscos & 22 \\\\ \\hline
 & \\hspace{1.2cm}\\textbullet\\ Roadmap Todas as Etapas & 23 \\\\ \\hline
 & \\hspace{1.2cm}\\textbullet\\ Detalhes de Perigos & 24 \\\\ \\hline
\\end{tabularx}
\\clearpage
`;
}

function gerarInformacoesContato(dados: DadosApreciacao): string {
  const ar = arCampos(dados);
  const criado = obterCriadoPor(dados);
  const aprovado = obterAprovadoPor(dados);
  const descricao = ar.descricaoRelatorio || dados.descricao || 'Not Available';
  return `
\\arSection{Informações de Contato}
\\textbf{Contato:}\\\\
${escapeLatex(obterCliente(dados))}\\\\
${escapeLatex(texto(dados.maquina?.site || dados.maquina?.local, ''))}

\\vspace{0.8cm}
\\textbf{Criado por:}\\\\
${escapeLatex(criado.nome)}\\\\
${escapeLatex(criado.funcao)}

\\vspace{0.8cm}
\\textbf{Aprovado por:}\\\\
${escapeLatex(aprovado.nome)}\\\\
${escapeLatex(aprovado.funcao)}

\\vspace{0.8cm}
\\textbf{Descrição do Relatório:}\\\\
${escapeLatexComQuebras(descricao)}
\\clearpage
`;
}

function gerarListaMaquinas(dados: DadosApreciacao): string {
  return `
\\arSection{Lista de Máquinas}
\\vspace{1.2cm}
\\begin{center}
\\Large\\textbf{1. ${escapeLatex(obterMaquina(dados))}}
\\end{center}
\\clearpage
`;
}

function gerarResumoTodasMaquinas(dados: DadosApreciacao, riscos: Risco[]): string {
  const maquina = obterMaquina(dados);
  let ordemResumo = 0;
  const linhas = riscos.flatMap((risco, index) => modosRisco(dados, risco).map((modo) => {
    ordemResumo += 1;
    const inicial = obterAvaliacaoModo(risco, modo, false);
    const residual = obterAvaliacaoModo(risco, modo, true);
    return `${ordemResumo} & ${escapeLatex(`HZD-${dados.numero || 'AR'}-${String(risco.numero || index + 1).padStart(4, '0')}`)} & ${escapeLatex(risco.eventoPerigoso || risco.parte || 'Perigo identificado')} & ${escapeLatex(modo)} & ${celulaHrnResumo(inicial.hrn)} & ${celulaHrnResumo(residual.hrn)} \\\\ \\hline`;
  })).join('\n');

  return `
\\arSection{Resumo da Classificação de Risco - Todas as Máquinas}
\\arCyanBand{${escapeLatex(maquina)} / ${escapeLatex(obterCriadoPor(dados).nome)} - ${escapeLatex(dados.tipoMaquina || dados.maquina?.modelo || 'Máquina')}}
\\vspace{0.25cm}

{\\small
\\begin{longtable}{|p{0.8cm}|p{2.2cm}|p{6.4cm}|p{2.4cm}|p{1.7cm}|p{1.7cm}|}
\\hline
\\rowcolor{arGray}\\textbf{Ord.} & \\textbf{ID} & \\textbf{Nome dos perigos} & \\textbf{Modo da Máquina} & \\textbf{HRN Inicial} & \\textbf{HRN Residual} \\\\ \\hline
${linhas || `1 & - & ${escapeLatex('Sem riscos cadastrados')} & - & - & - \\\\ \\hline`}
\\end{longtable}
}
\\clearpage
`;
}

function gerarPrefacio(dados: DadosApreciacao): string {
  const ar = arCampos(dados);
  const normasNacionais = [...AR_NORMAS_NACIONAIS_PADRAO, ...(ar.normasNacionais || [])]
    .map((norma) => `\\item ${escapeLatex(norma)}`).join('\n');
  const normasInternacionais = [...AR_NORMAS_INTERNACIONAIS_PADRAO, ...(ar.normasInternacionais || [])]
    .map((norma) => `\\item ${escapeLatex(norma)}`).join('\n');

  const contatosCliente = ar.contatosCliente.length > 0
    ? ar.contatosCliente.map((c) => `${escapeLatex(c.nome)} & ${escapeLatex(c.funcao)} & ${escapeLatex(c.contato)} \\\\ \\hline`).join('\n')
    : ` & & \\\\ \\hline`;
  const contatosTecnicum = ar.contatosTecnicum.length > 0
    ? ar.contatosTecnicum.map((c) => `${escapeLatex(c.nome)} & ${escapeLatex(c.funcao)} & ${escapeLatex(c.contato)} \\\\ \\hline`).join('\n')
    : `${escapeLatex(obterCriadoPor(dados).nome)} & ${escapeLatex(obterCriadoPor(dados).funcao)} & \\\\ \\hline`;

  return `
\\thispagestyle{plain}
\\begin{center}
{\\Large\\bfseries SUMÁRIO}
\\end{center}

1 \\hspace{0.6cm} INFORMAÇÕES DO DOCUMENTO\\\\
1.1 \\hspace{0.4cm} Conteúdos do documento\\\\
1.2 \\hspace{0.4cm} Exclusão de responsabilidade\\\\
1.3 \\hspace{0.4cm} Relação de contatos no cliente e tec.nicum\\\\
1.4 \\hspace{0.4cm} Referência Técnica sobre as Normas de Segurança\\\\
2 \\hspace{0.6cm} INFORMAÇÕES GERAIS PARA APRECIAÇÃO DE RISCO\\\\
3 \\hspace{0.6cm} AVALIAÇÃO DE RISCOS E PROPOSTAS DE REDUÇÃO DE RISCOS\\\\
3.1 \\hspace{0.4cm} Hazard Rating Number (HRN)\\\\
3.2 \\hspace{0.4cm} Estimativa de Riscos\\\\
3.3 \\hspace{0.4cm} Classificação do Grau de Risco\\\\
3.4 \\hspace{0.4cm} Observações do relatório\\\\
3.5 \\hspace{0.4cm} Segurança Funcional\\\\
4 \\hspace{0.6cm} RELATÓRIO DE APRECIAÇÃO DE RISCOS
\\clearpage

\\noindent\\textbf{1 \\hspace{0.6cm} INFORMAÇÕES DO DOCUMENTO}
Este documento descreve a metodologia sistemática para a realização de avaliações de risco em conformidade com a ISO 12100 e, quando aplicável, a determinação dos requisitos de segurança funcional em alinhamento com as normas ISO 13849-1 e IEC 62061.

\\vspace{0.25cm}
\\textbf{1.1 \\hspace{0.4cm} Conteúdos do documento}
A metodologia é baseada no sistema Hazard Rating Number (HRN), considerando os parâmetros de risco em formato ponderado para fornecer uma estimativa reprodutível, transparente e rastreável dos perigos avaliados.

Em conformidade com a ISO 12100, a metodologia possibilita uma avaliação passo a passo dos perigos associados às máquinas ao longo de todo o seu ciclo de vida: projeto, instalação, operação, manutenção e descomissionamento.

Para perigos que exigem medidas por meio de sistemas de controle, a metodologia integra a determinação dos requisitos de segurança funcional, incluindo a definição do Nível de Desempenho requerido (PLr), de acordo com a ISO 13849-1, e, quando apropriado, do Nível de Integridade de Segurança (SIL), conforme a IEC 62061.

\\clearpage
\\textbf{1.2 \\hspace{0.4cm} Exclusão de responsabilidade}
As funcionalidades descritas da(s) máquina(s) ou equipamento(s) são baseadas nos dados fornecidos pelo fabricante ou proprietário da máquina e nas informações coletadas no local durante a apreciação de riscos. Este relatório auxilia o responsável no atendimento aos requisitos aplicáveis, mas não garante nem confirma, por si só, a conformidade final da máquina.

Não está sendo avaliada a resistência dos materiais das proteções fixas, das proteções móveis, dos dispositivos eletromecânicos instalados e das dimensões dos meios de acesso permanentes.

Outros perigos citados na ABNT NBR ISO 12100 não estão sendo avaliados pois necessitam de uma avaliação específica, por exemplo: transporte aéreo de cargas, espaços confinados, análise de EPI, laudo de ruído, laudo de ancoragem, ergonomia, radiação, vibração, vasos de pressão, materiais e substâncias perigosas, infraestrutura civil, classificação de área, perigos associados ao ambiente e estabilidade da máquina.

\\clearpage
\\textbf{1.3 \\hspace{0.4cm} Relação de contatos no cliente e tec.nicum}
\\arBand{PESSOAS DE CONTATO NO CLIENTE}
\\begin{tabularx}{\\linewidth}{|X|X|X|}
\\hline
\\rowcolor{arGray}\\textbf{Nome} & \\textbf{Função} & \\textbf{Dados de contato} \\\\ \\hline
${contatosCliente}
\\end{tabularx}

\\vspace{0.3cm}
\\arBand{PESSOAS DE CONTATO NO tec.nicum}
\\begin{tabularx}{\\linewidth}{|X|X|X|}
\\hline
\\rowcolor{arGray}\\textbf{Nome} & \\textbf{Função} & \\textbf{Dados de contato} \\\\ \\hline
${contatosTecnicum}
\\end{tabularx}

\\clearpage
\\textbf{1.4 \\hspace{0.4cm} Referência Técnica sobre as Normas de Segurança}
\\textbf{NACIONAIS}
\\begin{itemize}
${normasNacionais}
\\end{itemize}

\\clearpage
\\textbf{INTERNACIONAIS}
\\begin{itemize}
${normasInternacionais}
\\end{itemize}

\\clearpage
\\textbf{2 \\hspace{0.6cm} INFORMAÇÕES GERAIS PARA APRECIAÇÃO DE RISCO}
A Norma Regulamentadora NR12 e seus anexos definem referências técnicas, princípios fundamentais e medidas de proteção para garantir a saúde e integridade física dos trabalhadores nas fases de projeto e utilização de máquinas e equipamentos.

Pelo processo iterativo de apreciação e redução de riscos, devem ser determinados os limites da máquina, identificados os perigos, estimados e avaliados os riscos e definidas medidas para eliminar perigos ou reduzir riscos associados.

O fabricante de máquinas ou seu representante autorizado deve assegurar que a apreciação de riscos seja cumprida a fim de determinar os requisitos de saúde e segurança que são aplicados às máquinas. As máquinas devem ser projetadas e construídas levando em consideração os resultados da apreciação de riscos.

\\begin{itemize}
\\item Determinar os limites da máquina, incluindo uso previsto e mau uso previsível;
\\item Identificar perigos que possam ser gerados pela máquina e situações associadas ao perigo;
\\item Estimar os riscos, considerando severidade de possíveis lesões, probabilidade de acontecimento, frequência de exposição e número de pessoas;
\\item Avaliar os riscos, visando determinar quando a redução de risco é requerida;
\\item Eliminar os perigos ou reduzir os riscos associados por aplicação de medidas protetoras.
\\end{itemize}

\\clearpage
\\textbf{3 \\hspace{0.6cm} AVALIAÇÃO DE RISCOS E PROPOSTAS DE REDUÇÃO DE RISCOS}
tec.nicum utiliza normas, regulamentos e especificações técnicas aplicáveis como orientação para tratar perigos de máquinas e medidas de controle recomendadas.

\\vspace{0.25cm}
\\textbf{3.1 \\hspace{0.4cm} Hazard Rating Number (HRN)}
O HRN é calculado pela multiplicação dos parâmetros: Probabilidade de Ocorrência (PO/LO), Frequência de Exposição (FE), Grau de Severidade (GS/DPH) e Número de Pessoas Expostas (NP/NPE).

\\begin{center}
\\Large\\textbf{HRN = LO $\\times$ DPH $\\times$ FE $\\times$ NPE}
\\end{center}

\\vspace{0.25cm}
\\textbf{3.2 \\hspace{0.4cm} Estimativa de Riscos}
tec.nicum utiliza o HRN (Hazard Rating Number), método numérico definido por Chris Steel, para estimar riscos avaliando consequências de um evento em termos de danos para as pessoas.

Os pesos apresentados a seguir são utilizados para avaliar a exposição de pessoas a situações perigosas previsíveis considerando a operação normal, tarefas de setup, limpeza, intervenção de manutenção e solução de problemas. A estimativa de risco deve sempre considerar o pior cenário razoavelmente previsível dentro do modo de máquina analisado.

\\clearpage

\\begin{center}
\\begin{tabular}{|p{8cm}|p{2.2cm}|}
\\hline
\\rowcolor{arGray}\\textbf{Grau de Severidade (GS)} & \\textbf{Peso} \\\\ \\hline
Arranhão / Contusão / Escoriação & 0.1 \\\\ \\hline
Dilaceração / Cortes / Enfermidade leve & 0.5 \\\\ \\hline
Fratura ossos menores / Enfermidade moderada & 1 \\\\ \\hline
Fratura ossos importantes / Enfermidade grave & 2 \\\\ \\hline
Perda de 1 membro / olho & 4 \\\\ \\hline
Perda de 2 membros / olhos & 8 \\\\ \\hline
Enfermidade permanente ou crítica & 12 \\\\ \\hline
Fatalidade & 15 \\\\ \\hline
\\end{tabular}
\\end{center}

\\vspace{0.2cm}
\\begin{center}
\\begin{tabular}{|p{8cm}|p{2.2cm}|}
\\hline
\\rowcolor{arGray}\\textbf{Frequência de Exposição (FE)} & \\textbf{Peso} \\\\ \\hline
Anualmente & 0.5 \\\\ \\hline
Mensalmente & 1 \\\\ \\hline
Semanalmente & 1.5 \\\\ \\hline
Diariamente & 2.5 \\\\ \\hline
Em termos de hora & 4 \\\\ \\hline
Constantemente & 5 \\\\ \\hline
\\end{tabular}
\\end{center}

\\clearpage
\\textbf{3.2 \\hspace{0.4cm} Estimativa de Riscos (continuação)}
O avaliador registra para cada perigo identificado os fatores utilizados no cálculo do I-HRN e, após a definição das medidas de redução de risco, repete o cálculo para o R-HRN. A rastreabilidade entre condição inicial, recomendação, implementação e risco residual permite priorização das ações corretivas e auditoria técnica do relatório.

\\vspace{0.25cm}
\\begin{center}
\\begin{tabular}{|p{8cm}|p{2.2cm}|}
\\hline
\\rowcolor{arGray}\\textbf{Probabilidade de Ocorrência (PO)} & \\textbf{Peso} \\\\ \\hline
Quase impossível & 0.033 \\\\ \\hline
Altamente improvável & 0.5 \\\\ \\hline
Improvável & 1.5 \\\\ \\hline
Possível & 2 \\\\ \\hline
Alguma chance & 5 \\\\ \\hline
Provável & 8 \\\\ \\hline
Muito provável & 10 \\\\ \\hline
Certo & 15 \\\\ \\hline
\\end{tabular}
\\end{center}

\\vspace{0.2cm}
\\begin{center}
\\begin{tabular}{|p{8cm}|p{2.2cm}|}
\\hline
\\rowcolor{arGray}\\textbf{Número de Pessoas Expostas ao Risco (NP)} & \\textbf{Peso} \\\\ \\hline
1-2 pessoas & 1 \\\\ \\hline
3-7 pessoas & 2 \\\\ \\hline
8-15 pessoas & 4 \\\\ \\hline
16-50 pessoas & 8 \\\\ \\hline
Mais que 50 pessoas & 12 \\\\ \\hline
\\end{tabular}
\\end{center}

\\clearpage
\\textbf{3.3 \\hspace{0.4cm} Classificação do Grau de Risco}
\\begin{tabularx}{\\linewidth}{|p{3.2cm}|X|}
\\hline
\\rowcolor{arGreen}\\textbf{Admissível} & Risco muito baixo ou controlado, com risco residual aceitável após a aplicação das medidas. \\\\ \\hline
\\rowcolor{arYellow}\\textbf{Baixo} & Risco baixo sujeito à implementação efetiva de informações de uso, treinamentos, sinalização e medidas preventivas. \\\\ \\hline
\\rowcolor{arOrange}\\textbf{Significativo / Alto} & Deve-se tomar medidas preventivas e avaliá-las considerando viabilidade técnica e econômica. \\\\ \\hline
\\rowcolor{arRed}\\textbf{Inaceitável} & É necessário refazer o projeto ou implementar controles antes de operação. \\\\ \\hline
\\end{tabularx}

\\clearpage
\\textbf{3.4 \\hspace{0.4cm} Observações do relatório}
Para as máquinas abordadas nesta apreciação de riscos, cada componente individual ou atividade operacional deve alcançar o status de conforme. Caso algum componente ou atividade seja identificado como não conforme, o proprietário da máquina deve implementar as medidas de redução de risco recomendadas neste relatório.

As medidas propostas seguem a hierarquia de redução de riscos prevista na ISO 12100, priorizando soluções de projeto inerentemente seguras, medidas técnicas de proteção e, por fim, informações para uso. A aceitação do risco residual depende da implementação efetiva das recomendações e da validação das funções de segurança quando aplicável.

\\clearpage
\\textbf{3.5 \\hspace{0.4cm} Segurança Funcional}
Quando uma função de segurança é implementada como medida de controle, considera-se a ABNT NBR ISO 13849-1:2019 para avaliação das Partes de Sistemas de Comando Relacionadas à Segurança (SRP/CS), incluindo severidade (S), frequência (F), possibilidade de evitar o dano (P), categoria e PLr.

Os parâmetros S, F e P são utilizados para apoiar a determinação do nível de desempenho requerido para a função de segurança proposta. A validação final deve considerar a arquitetura do sistema, a confiabilidade dos componentes, cobertura diagnóstica e eventuais falhas de causa comum.

\\begin{center}
\\begin{tabularx}{0.82\\linewidth}{|p{2.2cm}|X|}
\\hline
\\rowcolor{arGray}\\textbf{S} & \\textbf{Severidade do ferimento} \\\\ \\hline
S1 & Lesão leve, geralmente reversível \\\\ \\hline
S2 & Grave, geralmente irreversível, de uma ou mais pessoas e morte \\\\ \\hline
\\rowcolor{arGray}\\textbf{F} & \\textbf{Frequência e/ou tempo de exposição} \\\\ \\hline
F1 & De raramente a nunca \\\\ \\hline
F2 & De frequentemente a continuamente \\\\ \\hline
\\rowcolor{arGray}\\textbf{P} & \\textbf{Possibilidade de evitar o dano} \\\\ \\hline
P1 & Possível sob certas condições \\\\ \\hline
P2 & Praticamente impossível \\\\ \\hline
\\end{tabularx}
\\end{center}

\\clearpage
\\textbf{3.5 \\hspace{0.4cm} Segurança Funcional (continuação)}
Para as funções de segurança recomendadas neste relatório, a implementação definitiva deve ser verificada em projeto detalhado, considerando arquitetura, interface elétrica, reset, partida, prevenção de rearranque inesperado e validação após montagem. Quando houver intertravamentos, comandos de parada, monitoramento de portas ou bloqueio de energia, a comprovação do desempenho requerido permanece responsabilidade da solução final adotada.

\\vspace{0.25cm}
\\begin{tabularx}{0.88\\linewidth}{|p{2.8cm}|X|}
\\hline
\\rowcolor{arGray}\\textbf{Elemento} & \\textbf{Objetivo de verificação} \\\\ \\hline
Categoria / arquitetura & Confirmar coerência da estrutura do circuito com a função de segurança proposta. \\\\ \\hline
Diagnóstico & Confirmar meios de detecção de falhas e reação segura. \\\\ \\hline
Componente de campo & Verificar adequação de chaves, sensores, contatores e módulos. \\\\ \\hline
Validação & Ensaiar a função implementada e registrar o resultado da verificação final. \\\\ \\hline
\\end{tabularx}
\\clearpage
`;
}

function gerarDetalhesMaquina(dados: DadosApreciacao, riscos: Risco[]): string {
  const ar = arCampos(dados);
  const detalhes = ar.detalhesMaquina;
  const limites = ar.limitesMaquina;
  const fotos = fotosMaquinaFallback(dados, riscos);
  const blocoFotos = fotos.length >= 2
    ? `\\begin{tabularx}{\\linewidth}{XX}
\\includegraphics[width=0.48\\linewidth,height=5.35cm,keepaspectratio]{${fotos[0]}} &
\\includegraphics[width=0.48\\linewidth,height=5.35cm,keepaspectratio]{${fotos[1]}} \\\\
\\end{tabularx}

\\vspace{0.2cm}`
    : '';
  const descricao = texto(detalhes.descricao || dados.maquina?.descricao || dados.descricao);
  return `
\\arSection{Análise e Avaliação de Riscos}
\\arCyanBand{${escapeLatex(obterMaquina(dados))}}
\\arTitle{Detalhes da Máquina}
${blocoFotos}
\\begin{tabularx}{\\linewidth}{|p{5.2cm}|X|}
\\hline
Tipo de Máquina & ${escapeLatex(texto(dados.tipoMaquina || dados.maquina?.modelo))} \\\\ \\hline
Fabricante da Máquina & ${escapeLatex(texto(dados.maquina?.fabricante))} \\\\ \\hline
Identificação (Nº de Série) & ${escapeLatex(texto(dados.identificacaoMaquina || dados.maquina?.serie || dados.ativo?.refSismanaNovo))} \\\\ \\hline
Localização & ${escapeLatex(texto(dados.maquina?.local || dados.setorMaquina))} \\\\ \\hline
Setor/Indústria & ${escapeLatex(texto(dados.setorMaquina || dados.maquina?.area))} \\\\ \\hline
Data de Colocação no Mercado & ${escapeLatex(texto(detalhes.dataColocacaoMercado))} \\\\ \\hline
Certificação (CE) & ${escapeLatex(texto(detalhes.certificacaoCe))} \\\\ \\hline
Data da inspeção in loco & ${escapeLatex(texto(detalhes.dataInspecaoInLoco || dados.dataEntrevista))} \\\\ \\hline
\\end{tabularx}

\\vspace{0.15cm}
\\textbf{Descrição}\\\\
${escapeLatexComQuebras(descricao)}

\\clearpage
\\arTitle{Limitação da máquina : ${escapeLatex(obterMaquina(dados))}}
\\textbf{Limites de uso}
\\begin{tabularx}{\\linewidth}{|p{5.2cm}|X|}
\\hline
Modo da Máquina & ${escapeLatex((limites.modosMaquina || []).join(', ') || texto(dados.modoOperacao))} \\\\ \\hline
Uso da máquina & ${escapeLatexComQuebras(texto(limites.usoMaquina || dados.funcaoMaquina || dados.limitesUso))} \\\\ \\hline
\\end{tabularx}

\\vspace{0.18cm}
\\textbf{Tarefas relacionadas e nível de treinamento dos usuários}
\\begin{tabularx}{\\linewidth}{|p{5.2cm}|X|}
\\hline
Número de Operadores & ${escapeLatex(texto(limites.numeroOperadores || dados.operadorEntrevistado))} \\\\ \\hline
Operador e preparador & ${escapeLatexComQuebras(texto(limites.treinamentoUsuarios || dados.cargoOperador))} \\\\ \\hline
\\end{tabularx}

\\vspace{0.18cm}
\\textbf{Limites de espaço}
\\begin{tabularx}{\\linewidth}{|p{5.2cm}|X|}
\\hline
Tamanho da Máquina & ${escapeLatex(texto(limites.tamanhoMaquina || dados.comprimentoHorizontal))} \\\\ \\hline
Interface da Fonte de Alimentação & ${escapeLatexComQuebras(texto(limites.interfaceFonteAlimentacao || [dados.tensaoAcionamento, dados.tensaoComando, dados.potencia].filter(Boolean).join('\\n')))} \\\\ \\hline
\\end{tabularx}

\\vspace{0.18cm}
\\textbf{Limites de tempo}
\\begin{tabularx}{\\linewidth}{|p{5.2cm}|X|}
\\hline
Vida útil da máquina & ${escapeLatex(texto(limites.vidaUtilMaquina || dados.limitacoes))} \\\\ \\hline
Intervalo de serviço recomendado & ${escapeLatex(texto(limites.intervaloServicoRecomendado))} \\\\ \\hline
\\end{tabularx}
`;
}

function gerarResumoMaquina(dados: DadosApreciacao, riscos: Risco[]): string {
  let ordemResumo = 0;
  const linhas = riscos.flatMap((risco, index) => modosRisco(dados, risco).map((modo) => {
    ordemResumo += 1;
    const inicial = obterAvaliacaoModo(risco, modo, false);
    const residual = obterAvaliacaoModo(risco, modo, true);
    return `${ordemResumo} & ${escapeLatex(`HZD-${dados.numero || 'AR'}-${String(risco.numero || index + 1).padStart(4, '0')}`)} & ${escapeLatex(risco.eventoPerigoso || risco.parte || 'Perigo identificado')} & ${escapeLatex(modo)} & ${celulaHrnResumo(inicial.hrn)} & ${celulaHrnResumo(residual.hrn)} \\\\ \\hline`;
  })).join('\n');

  return `
\\clearpage
\\arBlueBand{Lista resumo de classificação de riscos}
\\arCyanBand{${escapeLatex(obterMaquina(dados))}}
\\addcontentsline{toc}{subsubsection}{Resumo de Riscos}
{\\small
\\begin{longtable}{|p{0.8cm}|p{2.1cm}|p{6.3cm}|p{2.3cm}|p{1.7cm}|p{1.7cm}|}
\\hline
\\rowcolor{arGray}\\textbf{Ord.} & \\textbf{ID} & \\textbf{Nome dos perigos} & \\textbf{Modo da Máquina} & \\textbf{HRN Inicial} & \\textbf{HRN Residual} \\\\ \\hline
${linhas || `1 & - & ${escapeLatex('Sem riscos cadastrados')} & - & - & - \\\\ \\hline`}
\\end{longtable}
}
`;
}

function gerarRoadmap(dados: DadosApreciacao, riscos: Risco[]): string {
  const grupos = new Map<string, Risco[]>();
  riscos.forEach((risco) => {
    const chave = risco.tipoPerigo || 'PERIGOS MECÂNICOS';
    grupos.set(chave, [...(grupos.get(chave) || []), risco]);
  });

  const conteudo = [...grupos.entries()].map(([tipo, itens]) => {
    const linhas = itens.map((risco, index) => {
      const extras = (risco as any).origemEspecifica || {};
      const passo = extras.passo || `${String(index + 1).padStart(2, '0')} - ${risco.parte || risco.eventoPerigoso || 'Passo avaliado'}`;
      const resultado = risco.conformeAtual ? 'Sim, Conforme' : 'Perigo identificado';
      const notas = risco.descricaoCondicaoAtual || risco.perigosEncontrados || risco.eventoPerigoso || '';
      return `${escapeLatex(texto(passo, 'Passo avaliado'))} & ${escapeLatex(texto(resultado, 'Perigo identificado'))} & ${escapeLatexComQuebras(texto(notas, ''))} \\\\ \\hline`;
    }).join('\n');

    return `
\\arBand{${escapeLatex(String(tipo).toUpperCase())}}
\\begin{longtable}{|p{5.1cm}|p{3.0cm}|p{6.6cm}|}
\\hline
\\rowcolor{arGray}\\textbf{Passo} & \\textbf{Resultado} & \\textbf{Notas do passo} \\\\ \\hline
${linhas}
\\end{longtable}
`;
  }).join('\n');

  return `
\\clearpage
\\arBlueBand{${escapeLatex(texto(obterTituloWorkflow(dados), 'Workflow'))}}
\\addcontentsline{toc}{subsubsection}{Roadmap Todas as Etapas}
${conteudo || 'Not Available'}
\\clearpage
`;
}

function gerarTabelaHrn(risco: Risco, modo: string, residual = false): string {
  const avaliacao = obterAvaliacaoModo(risco, modo, residual);
  const probabilidade = avaliacao.probabilidade;
  const frequencia = avaliacao.frequencia;
  const gravidade = avaliacao.gravidade;
  const exposicao = avaliacao.exposicao;
  const hrn = avaliacao.hrn;
  const classificacao = classificarAr(hrn);
  const formula = residual ? 'R-HRN = LO * DPH * FE * NPE' : 'I-HRN = LO * DPH * FE * NPE';
  const tituloNivel = residual ? 'Nível de Risco\\newline Admissível' : `Nível de Risco\\newline ${escapeLatex(classificacao.texto)}`;
  const descricaoProbabilidadeResidual = descricaoHrn('LO', probabilidade) === 'Quase Impossível (0,033)'
    ? 'Quase Impossível (possível em circunstâncias extremas) (0.033)'
    : escapeLatex(descricaoHrn('LO', probabilidade));
  const descricaoProbabilidade = residual
    ? descricaoProbabilidadeResidual
    : escapeLatex(descricaoHrn('LO', probabilidade));

  return `
${residual ? `\\begin{tabularx}{\\linewidth}{|p{3.1cm}|X|}
\\hline
\\cellcolor{arCyan}Status de conformidade & \\cellcolor{green!45}{Risco em conformidade após a consideração de todas as medidas aplicáveis} \\\\ \\hline
\\end{tabularx}
` : ''}
\\begin{tabularx}{\\linewidth}{|p{2.9cm}|p{2.3cm}|p{0.8cm}|p{1.6cm}|p{0.7cm}|p{2.2cm}|p{0.7cm}|p{1.5cm}|p{0.7cm}|}
\\hline
\\rowcolor{arCyan}
Modo da Máquina & Probabilidade de Ocorrência & LO & Frequência de Exposição & FE & Grau de Severidade & DPH & Número de Pessoas em risco & NPE \\\\ \\hline
\\rowcolor{arGray}
${escapeLatex(modo)} & ${descricaoProbabilidade} & ${numero(probabilidade)} & ${escapeLatex(descricaoHrn('FE', frequencia))} & ${numero(frequencia)} & ${escapeLatex(descricaoHrn('DPH', gravidade))} & ${numero(gravidade)} & ${escapeLatex(descricaoHrn('NPE', exposicao))} & ${numero(exposicao)} \\\\ \\hline
\\multicolumn{5}{|>{\\centering\\arraybackslash}p{8.4cm}|}{\\cellcolor{arCyan}Número de Classificação do Perigo\\newline(${formula})} &
\\multicolumn{3}{>{\\centering\\arraybackslash}p{4.7cm}|}{\\cellcolor{${classificacao.cor}}${tituloNivel}} &
\\cellcolor{${classificacao.cor}}\\textbf{${numeroHrn(hrn)}} \\\\ \\hline
\\end{tabularx}${residual ? '' : '\\vspace{0.16cm}'}
`;
}

function gerarRecomendacao(risco: Risco): string {
  const recomendacoes = [
    risco.medidasControle,
    risco.adequacoesTecnicas,
    risco.adequacoesNormativas,
  ].filter(Boolean) as string[];
  const referencias = risco.exigenciasNormativas || 'Referências normativas: conforme normas técnicas aplicáveis.';
  const srpcs = risco.exibirSRPCS !== false && (risco.plr || risco.categoriaSeguranca || risco.severidadeSRPCS || risco.frequenciaSRPCS || risco.possibilidadeSRPCS);

  return `
\\arBand{Recomendação de Redução de Risco}
\\begin{longtable}{|p{3.0cm}|p{2.8cm}|p{9.2cm}|}
\\hline
\\rowcolor{arGray}\\textbf{Tipo de medida} & \\textbf{Modo da Máquina} & \\textbf{Descrição da medida} \\\\ \\hline
${recomendacoes.length > 0
  ? recomendacoes.map((rec, index) => `${index === 0 ? 'Medida de controle' : 'Proteção (medida técnica)'} & ${escapeLatex(risco.cicloVida || 'Normal')} & ${escapeLatexComQuebras(rec)}\\newline\\newline ${escapeLatexComQuebras(referencias)} \\\\ \\hline`).join('\n')
  : `Medida de controle & ${escapeLatex(risco.cicloVida || 'Normal')} & ${escapeLatex('Not Available')} \\\\ \\hline`}
\\end{longtable}
${srpcs ? `
\\textbf{Determinação do nível de desempenho requerido (PLr) relacionado à segurança da função de segurança}
\\begin{center}
\\begin{tabular}{|c|c|c|c|c|c|}
\\hline
\\rowcolor{arGray}\\textbf{PLr determinada} & \\textbf{S} & \\textbf{F} & \\textbf{P} & \\textbf{Categoria} & \\textbf{PLr} \\\\ \\hline
${escapeLatex(risco.plr || '-')} & ${escapeLatex(risco.severidadeSRPCS || '-')} & ${escapeLatex(risco.frequenciaSRPCS || '-')} & ${escapeLatex(risco.possibilidadeSRPCS || '-')} & ${escapeLatex(risco.categoriaSeguranca || '-')} & ${escapeLatex(risco.plr || '-')} \\\\ \\hline
\\end{tabular}
\\end{center}` : ''}
`;
}

function gerarPaginaInformacaoPerigo(dados: DadosApreciacao, risco: Risco, index: number, id: string, fotos: string[]): string {
  const extras = (risco as any).origemEspecifica || {};
  const legendaPrincipal = legendaFotoPrincipal(risco);
  const tituloFoto = fotos.length > 0
    ? `\\begin{minipage}[t]{0.50\\linewidth}
\\includegraphics[width=\\linewidth,height=7.6cm,keepaspectratio]{${fotos[0]}}\\\\[-0.05cm]
${legendaPrincipal ? `\\colorbox{arCyan}{\\parbox{\\dimexpr\\linewidth-2\\fboxsep}{\\strut ${escapeLatex(legendaPrincipal)}}}` : ''}
\\end{minipage}`
    : `\\fbox{\\parbox[c][7.2cm][c]{0.52\\linewidth}{\\centering Imagem não disponível}}`;
  const detalhesDireita = `
\\begin{minipage}[t]{0.47\\linewidth}
\\begin{tabularx}{\\linewidth}{|p{2.95cm}|X|}
\\hline
\\cellcolor{arCyan}Resultado & ${risco.conformeAtual ? escapeLatex('Sim, Conforme') : '\\cellcolor{arOrange}\\textcolor{black}{Perigo identificado}'} \\\\ \\hline
\\cellcolor{arCyan}Nome do Perigo & ${escapeLatex(risco.eventoPerigoso || risco.parte || 'Not Available')} \\\\ \\hline
\\cellcolor{arCyan}Tipo do Perigo & ${escapeLatex(risco.tipoPerigo || 'Not Available')} \\\\ \\hline
\\cellcolor{arCyan}Fonte do Perigo & ${escapeLatexComQuebras(risco.perigosEncontrados || 'Not Available')} \\\\ \\hline
\\cellcolor{arCyan}Consequências do Perigo & ${escapeLatexComQuebras(risco.consequencias || 'Not Available')} \\\\ \\hline
\\end{tabularx}
\\end{minipage}`;
  return `
\\arCyanBand{${escapeLatex(obterMaquina(dados))}}
\\begin{tabularx}{\\linewidth}{|p{3.4cm}|X|}
\\hline
\\rowcolor{arCyan}\\textbf{Roadmap} & ${escapeLatex(obterTituloWorkflow(dados))} \\\\ \\hline
\\end{tabularx}

\\vspace{0.2cm}
\\arBand{Informação do Perigo \\hfill ${escapeLatex(id)}}
\\addcontentsline{toc}{subsubsection}{${escapeLatex(risco.eventoPerigoso || risco.parte || id)}}
\\begin{tabularx}{\\linewidth}{|p{3.4cm}|X|}
\\hline
\\cellcolor{arCyan}Seção & ${escapeLatex(extras.secao || risco.tipoPerigo || 'PERIGOS MECÂNICOS')} \\\\ \\hline
\\cellcolor{arCyan}Subseção & ${escapeLatex(extras.subsecao || risco.categoria || risco.parte || 'Not Available')} \\\\ \\hline
\\cellcolor{arCyan}Passo & ${escapeLatex(extras.passo || `${String(index + 1).padStart(2, '0')} - ${risco.parte || 'Passo avaliado'}`)} \\\\ \\hline
\\end{tabularx}

\\vspace{0.22cm}
${tituloFoto}\\hfill${detalhesDireita}

\\vspace{0.18cm}
\\begin{tabularx}{\\linewidth}{|p{3.4cm}|X|}
\\hline
\\cellcolor{arCyan}Perigo Inicial & ${escapeLatexComQuebras(risco.descricaoCondicaoAtual || risco.eventoPerigoso || 'Not Available')} \\\\ \\hline
\\end{tabularx}
\\clearpage
`;
}

function gerarPaginaImagensAdicionais(risco: Risco, id: string, fotos: string[]): string {
  if (fotos.length <= 1) return '';
  const adicionais = fotos.slice(1);
  const legendas = legendasFotosAdicionais(risco);
  const blocos = adicionais.map((foto, index) => {
    const legenda = legendas[index] || '';
    return `\\begin{minipage}[t]{0.31\\linewidth}
\\centering
\\includegraphics[width=\\linewidth,height=5.0cm,keepaspectratio]{${foto}}\\\\[-0.02cm]
${legenda ? `{\\small ${escapeLatex(legenda)}}` : ''}
\\end{minipage}`;
  });
  const colunas = blocos.join(' \\hfill ');
  return `
\\arBand{Imagens adicionais do perigo \\hfill ${escapeLatex(id)}}
${colunas}
\\clearpage
`;
}

function gerarPaginaAvaliacaoInicial(risco: Risco, modos: string[], id: string, incluirRecomendacao: boolean): string {
  return `
\\arBand{Estimativa e Avaliação de Risco \\hfill ${escapeLatex(id)}}
\\textbf{Número de Classificação do Perigo Inicial (cálculo I-HRN)}
${modos.map((modo) => gerarTabelaHrn(risco, modo, false)).join('\n')}
${incluirRecomendacao ? gerarRecomendacao(risco) : ''}
\\clearpage
`;
}

function gerarPaginaResidual(risco: Risco, modos: string[], id: string): string {
  return `
\\arBand{Número de Classificação de Perigo Residual (R-HRN) \\hfill ${escapeLatex(id)}}
A classificação de risco residual só é alcançada através da implementação completa e adequada de todas as medidas de mitigação de risco.

${modos.map((modo) => gerarTabelaHrn(risco, modo, true)).join('\n')}
\\clearpage
`;
}

function gerarFichaRisco(dados: DadosApreciacao, risco: Risco, index: number): string {
  const id = `HZD-${dados.numero || 'AR'}-${String(risco.numero || index + 1).padStart(4, '0')}`;
  const fotos = fotosRisco(risco);
  const modos = modosRisco(dados, risco);
  const recomendacoes = [risco.medidasControle, risco.adequacoesTecnicas, risco.adequacoesNormativas].filter(Boolean).join('\n');
  const recomendacaoEmPaginaSeparada = Boolean(risco.exibirSRPCS) || Boolean(risco.adequacoesTecnicas) || Boolean(risco.adequacoesNormativas) || recomendacoes.length > 520;

  return [
    gerarPaginaInformacaoPerigo(dados, risco, index, id, fotos),
    gerarPaginaImagensAdicionais(risco, id, fotos),
    gerarPaginaAvaliacaoInicial(risco, modos, id, !recomendacaoEmPaginaSeparada),
    recomendacaoEmPaginaSeparada ? `${gerarRecomendacao(risco)}\\clearpage\n` : '',
    gerarPaginaResidual(risco, modos, id),
  ].join('\n');
}

function gerarDetalhesRiscos(dados: DadosApreciacao, riscos: Risco[]): string {
  return `
\\arBlueBand{Detalhes de Perigos}
\\addcontentsline{toc}{subsubsection}{Detalhes de Perigos}
${riscos.map((risco, index) => gerarFichaRisco(dados, risco, index)).join('\n')}
`;
}

export function gerarLatexAr(dados: DadosApreciacao, riscos: Risco[]): string {
  let latex = gerarPreambuloAr(dados);
  latex += `\\begin{document}\n`;
  latex += gerarCapaAr(dados);
  latex += gerarIndiceAr(dados);
  latex += gerarInformacoesContato(dados);
  latex += gerarListaMaquinas(dados);
  latex += gerarResumoTodasMaquinas(dados, riscos);
  latex += gerarPrefacio(dados);
  latex += gerarDetalhesMaquina(dados, riscos);
  latex += gerarResumoMaquina(dados, riscos);
  latex += gerarRoadmap(dados, riscos);
  latex += gerarDetalhesRiscos(dados, riscos);
  latex += `\\end{document}`;
  return latex;
}
